'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { UseGameTimerReturn } from '@/contexts/games/types';

/**
 * Универсальный хук для таймера игры
 */
export function useGameTimer(): UseGameTimerReturn {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Форматирование времени в читаемый вид (MM:SS)
  const formatTime = useCallback((timeInMs: number): string => {
    const totalSeconds = Math.floor(timeInMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  const formattedTime = formatTime(elapsedTime);

  // Обновление времени
  const updateTime = useCallback(() => {
    if (isRunning && !isPaused) {
      const now = Date.now();
      const elapsed = now - startTimeRef.current - pausedTimeRef.current;
      setElapsedTime(elapsed);
    }
  }, [isRunning, isPaused]);

  // Запуск таймера
  const start = useCallback(() => {
    if (!isRunning) {
      startTimeRef.current = Date.now();
      pausedTimeRef.current = 0;
      setElapsedTime(0);
    } else if (isPaused) {
      // Возобновление после паузы
      const pauseDuration = Date.now() - (startTimeRef.current + pausedTimeRef.current + elapsedTime);
      pausedTimeRef.current += pauseDuration;
    }
    
    setIsRunning(true);
    setIsPaused(false);
  }, [isRunning, isPaused, elapsedTime]);

  // Пауза таймера
  const pause = useCallback(() => {
    if (isRunning && !isPaused) {
      setIsPaused(true);
    }
  }, [isRunning, isPaused]);

  // Возобновление таймера
  const resume = useCallback(() => {
    if (isRunning && isPaused) {
      setIsPaused(false);
    }
  }, [isRunning, isPaused]);

  // Остановка таймера
  const stop = useCallback(() => {
    setIsRunning(false);
    setIsPaused(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Сброс таймера
  const reset = useCallback(() => {
    stop();
    setElapsedTime(0);
    startTimeRef.current = 0;
    pausedTimeRef.current = 0;
  }, [stop]);

  // Эффект для обновления времени
  useEffect(() => {
    if (isRunning && !isPaused) {
      intervalRef.current = setInterval(updateTime, 100); // Обновляем каждые 100мс для плавности
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isRunning, isPaused, updateTime]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    elapsedTime,
    formattedTime,
    isRunning,
    isPaused,
    start,
    pause,
    resume,
    stop,
    reset,
  };
}
